import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper.min.css';
import { Keyboard, Navigation, Lazy } from 'swiper';
import { Icon, useKeyDown } from '@sendible/design-system';
import SharedStateBridge, { MediaPreviewProps } from '@sendible/shared-state-bridge';
import { LightboxStyled, SwiperContainerBox } from './index.styles';

export const PREVIEW_LIGHTBOX_TEST_ID = 'previewmedias_test';

export const MediaPreview = (props: MediaPreviewProps) => {
  const { mediaList, initialMediaIndex } = props;

  const { t } = useTranslation('media_library');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const swiperRef = useRef(null) as React.RefObject<any>;

  const ariaMoveSliderLeftLabel = t('aria_move_slider_left');
  const ariaMoveSliderRightLabel = t('aria_move_slider_right');
  const ariaCloseLightboxLabel = t('aria_close_lightbox');
  const requestClose = () => {
    SharedStateBridge.updateSpecificKey('app', 'mediaPreview', {
      isMediaPreviewOpen: false,
      mediaList: [],
      initialMediaIndex: 0,
    });
  };

  useKeyDown(() => {
    requestClose();
  }, 'Escape');

  const onSlideChange = () => {
    const videos = document.querySelectorAll('video');

    Array.prototype.forEach.call(videos, (video) => video.pause());
  };

  const config = {
    className: 'lightbox__swiper',
    lazy: {
      loadPrevNext: true,
    },
    initialSlide: initialMediaIndex || 0,
    keyboard: {
      enabled: true,
      onlyInViewport: true,
    },
    modules: [Keyboard, Navigation, Lazy],
    navigation: {
      prevEl: '.swiper-button-prev',
      nextEl: '.swiper-button-next',
    },
    preloadImages: false,
    onSlideChange,
  };

  return createPortal(
    <LightboxStyled
      darkmode
      close={requestClose}
      testId={PREVIEW_LIGHTBOX_TEST_ID}
      ariaCloseLabel={ariaCloseLightboxLabel}
    >
      <SwiperContainerBox
        orientation="row"
        verticalGap="s32"
      >
        <Swiper
          {...config}
          ref={swiperRef}
        >
          {mediaList.map((media) => (
            <SwiperSlide key={media.url}>
              {media.type === 'video' && (
                // eslint-disable-next-line jsx-a11y/media-has-caption
                <video
                  className="swiper-lazy"
                  controls
                  tabIndex={-1}
                >
                  <source
                    src={media.url}
                    type="video/webm"
                  />
                </video>
              )}
              {media.type === 'image' && (
                <img
                  className="swiper-lazy"
                  data-src={media.url}
                  alt=""
                />
              )}
              <div className="swiper-lazy-preloader" />
            </SwiperSlide>
          ))}
          <button
            type="button"
            className="swiper-button-prev"
            tabIndex={0}
            aria-label={ariaMoveSliderLeftLabel}
          >
            <Icon
              color="brandLight"
              name="arrow_left"
            />
          </button>
          <button
            type="button"
            className="swiper-button-next"
            tabIndex={0}
            aria-label={ariaMoveSliderRightLabel}
          >
            <Icon
              color="brandLight"
              name="arrow_right"
            />
          </button>
        </Swiper>
      </SwiperContainerBox>
    </LightboxStyled>,
    document.getElementById('lightbox-root') as HTMLElement
  );
};
